import { useEffect, useState } from "react";

// Work center display configuration
const WORK_CENTER_CONFIG = {
  PM: { label: "Project Management", color: "from-blue-600 to-blue-800" },
  PA: { label: "Production Art", color: "from-purple-600 to-purple-800" },
  OPTI: { label: "Opti", color: "from-green-600 to-green-800" },
  ASSEMBLY: { label: "Assembly", color: "from-orange-600 to-orange-800" },
  // Combined Assembly/Opti - shows operations from both work centers
  "ASSEMBLY_OPTI": { label: "Assembly + Opti", color: "from-orange-500 via-green-500 to-green-600" },
  PAINT: { label: "Paint", color: "from-red-600 to-red-800" },
  "3D MODEL": { label: "3D Model", color: "from-cyan-600 to-cyan-800" },
  // Combined 3D Model + Paint - shows operations from both work centers
  "3DMODEL_PAINT": { label: "3D Model + Paint", color: "from-cyan-500 via-red-500 to-red-600" },
  FULFILLMNT: { label: "Fulfillment", color: "from-yellow-600 to-yellow-800" },
  QA: { label: "Quality Assurance", color: "from-pink-600 to-pink-800" },
};

// Display order for work centers
const WORK_CENTER_ORDER = [
  "PA",
  "ASSEMBLY_OPTI",  // Combined option appears first
  "ASSEMBLY",
  "OPTI",
  "3DMODEL_PAINT",  // Combined 3D Model + Paint
  "PM",
  "PAINT",
  "3D MODEL",
  "FULFILLMNT",
  "QA",
];

export default function WorkCenterSelector({
  allowedWorkCenters = [],
  selectedWorkCenter,
  onSelect,
  compact = false
}) {
  const [loading, setLoading] = useState(false);

  // Filter to only show allowed work centers, sorted by display order
  // Special handling for combined options: show them if user has access to BOTH
  const availableWorkCenters = WORK_CENTER_ORDER.filter((wc) => {
    if (wc === "ASSEMBLY_OPTI") {
      // Show combined option only if user has both ASSEMBLY and OPTI
      return allowedWorkCenters.includes("ASSEMBLY") && allowedWorkCenters.includes("OPTI");
    }
    if (wc === "3DMODEL_PAINT") {
      // Show combined option only if user has both 3D MODEL and PAINT
      return allowedWorkCenters.includes("3D MODEL") && allowedWorkCenters.includes("PAINT");
    }
    return allowedWorkCenters.includes(wc) && WORK_CENTER_CONFIG[wc];
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading work centers...</div>
      </div>
    );
  }

  // Compact mode - small indicator with change button
  if (compact && selectedWorkCenter) {
    const config = WORK_CENTER_CONFIG[selectedWorkCenter] || {};
    return (
      <div className="flex items-center gap-3 px-4 py-2 bg-slate-100 rounded-lg border border-gray-200">
        <span className="text-gray-500 text-sm">Work Center:</span>
        <div className={`px-3 py-1 rounded-md bg-gradient-to-r ${config.color || "from-gray-200 to-gray-300"}`}>
          <span className="text-gray-900 font-bold text-sm">{config.label || selectedWorkCenter}</span>
        </div>
        <button
          onClick={() => onSelect(null)}
          className="ml-auto text-xs text-gray-500 hover:text-gray-900 transition px-2 py-1 rounded hover:bg-slate-100"
        >
          Change
        </button>
      </div>
    );
  }

  // Full selector mode - large button cards
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Your Work Center</h2>
      <p className="text-gray-500 mb-8">Choose the area you're working in today</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
        {availableWorkCenters.map((wc) => {
          const config = WORK_CENTER_CONFIG[wc] || {};
          const isSelected = selectedWorkCenter === wc;

          return (
            <button
              key={wc}
              onClick={() => onSelect(wc)}
              className={`
                relative flex flex-col items-center justify-center
                p-4 md:p-6 rounded-xl
                bg-gradient-to-br ${config.color || "from-gray-200 to-gray-300"}
                border-2 transition-all duration-200
                hover:scale-105 hover:shadow-lg hover:shadow-black/30
                active:scale-95
                w-[160px] md:w-[180px] h-[70px] md:h-[80px]
                min-h-[60px]
                ${isSelected
                  ? "border-white ring-2 ring-white/50"
                  : "border-transparent hover:border-white/30"
                }
              `}
            >
              <span className="text-gray-900 font-bold text-base md:text-lg text-center leading-tight">
                {config.label || wc}
              </span>
              {isSelected && (
                <div className="absolute top-2 right-2 w-3 h-3 bg-white rounded-full" />
              )}
            </button>
          );
        })}
      </div>

      {availableWorkCenters.length === 0 && (
        <div className="text-red-400 mt-4">
          No work centers assigned. Please contact your administrator.
        </div>
      )}
    </div>
  );
}
